/* eslint-disable @typescript-eslint/no-unsafe-member-access */
//
//  SDKTypographyToken.ts
//  Supernova SDK
//
//  Created by Jiri Trecak.
//
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Imports
import { SupernovaError } from "../../core/errors/SDKSupernovaError";
import { TokenType } from "../enums/SDKTokenType";
import { DimensionToken } from "./SDKDimensionToken";
import { StringToken } from "./SDKStringToken";
import { TextDecorationToken } from "./SDKTextDecorationToken";
import { Token } from "./SDKToken";
import { v4 as uuidv4 } from "uuid";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: -  Object Definition
export class TypographyToken extends Token {
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Constructor
    constructor(versionId, baseToken, value, alias, properties, propertyValues) {
        super(baseToken, versionId, properties, propertyValues);
        this.tokenType = TokenType.typography;
        this.value = value;
        if (alias) {
            this.value.referencedTokenId = alias.id;
        }
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Static building
    static create(versionId, brandId, name, description, value, alias, referenceResolver, properties, propertyValues, reference) {
        const baseToken = {
            // @ts-expect-error TS(2322): Type 'undefined' is not assignable to type 'string... Remove this comment to see the full error message
            id: undefined, // Ommited id will create new token
            persistentId: uuidv4(),
            brandId,
            designSystemVersionId: versionId,
            type: TokenType.typography,
            meta: {
                name,
                description,
            },
            data: {},
            customPropertyOverrides: [],
        };
        if (value !== null && value !== undefined) {
            // Raw value
            const tokenValue = this.typographyValueFromDefinition(value, referenceResolver, reference);
            return new TypographyToken(versionId, baseToken, tokenValue, 
            // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
            undefined, properties, propertyValues);
        }
        if (alias) {
            // Aliased value - copy and create raw from reference
            const tokenValue = {
                fontFamily: alias.value.fontFamily,
                fontWeight: alias.value.fontWeight,
                fontSize: alias.value.fontSize,
                letterSpacing: alias.value.letterSpacing,
                lineHeight: alias.value.lineHeight,
                paragraphIndent: alias.value.paragraphIndent,
                paragraphSpacing: alias.value.paragraphSpacing,
                textDecoration: alias.value.textDecoration,
                textCase: alias.value.textCase,
                referencedTokenId: alias.id,
            };
            return new TypographyToken(versionId, baseToken, tokenValue, 
            // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
            undefined, properties, propertyValues);
        }
        throw SupernovaError.fromMessage("Typography Token must be created using value or alias, but none was provided");
    }
    static typographyValueFromDefinition(definition, referenceResolver, reference) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t;
        // No validation because
        const value = {}; // Empty container
        // If we have theme, then missing typography props should first try to fallback to base values, and then - to defaults
        // Double check that we have `base` === we are resolving theme (otherwise pass this info from upstream)
        const base = reference
            ? referenceResolver.lookupReferencedToken(reference)
            : null;
        // TODO: Ideallly should not compile without casting
        value.fontFamily = definition.fontFamily
            ? StringToken.stringValueFromDefinitionOrReference(definition.fontFamily, referenceResolver, TokenType.fontFamily)
            : ((_a = base === null || base === void 0 ? void 0 : base.value) === null || _a === void 0 ? void 0 : _a.fontFamily)
                ? (_b = base === null || base === void 0 ? void 0 : base.value) === null || _b === void 0 ? void 0 : _b.fontFamily
                : StringToken.stringValueFromDefinition("Roboto", TokenType.fontFamily);
        value.fontWeight = definition.fontWeight
            ? StringToken.stringValueFromDefinitionOrReference(definition.fontWeight, referenceResolver, TokenType.fontWeight)
            : ((_c = base === null || base === void 0 ? void 0 : base.value) === null || _c === void 0 ? void 0 : _c.fontWeight)
                ? (_d = base === null || base === void 0 ? void 0 : base.value) === null || _d === void 0 ? void 0 : _d.fontWeight
                : StringToken.stringValueFromDefinition("Regular", TokenType.fontWeight);
        value.fontSize = (definition.fontSize
            ? DimensionToken.dimensionValueFromDefinitionOrReference(definition.fontSize, referenceResolver, TokenType.fontSize)
            : ((_e = base === null || base === void 0 ? void 0 : base.value) === null || _e === void 0 ? void 0 : _e.fontSize)
                ? (_f = base === null || base === void 0 ? void 0 : base.value) === null || _f === void 0 ? void 0 : _f.fontSize
                : DimensionToken.dimensionValueFromDefinition("12px", TokenType.fontSize));
        value.letterSpacing = (definition.letterSpacing
            ? DimensionToken.dimensionValueFromDefinitionOrReference(definition.letterSpacing, referenceResolver, TokenType.letterSpacing)
            : ((_g = base === null || base === void 0 ? void 0 : base.value) === null || _g === void 0 ? void 0 : _g.letterSpacing)
                ? (_h = base === null || base === void 0 ? void 0 : base.value) === null || _h === void 0 ? void 0 : _h.letterSpacing
                : DimensionToken.dimensionValueFromDefinition("0", TokenType.letterSpacing));
        value.lineHeight = definition.lineHeight
            ? DimensionToken.dimensionValueFromDefinitionOrReference(definition.lineHeight, referenceResolver, TokenType.lineHeight)
            : ((_j = base === null || base === void 0 ? void 0 : base.value) === null || _j === void 0 ? void 0 : _j.lineHeight)
                ? (_k = base === null || base === void 0 ? void 0 : base.value) === null || _k === void 0 ? void 0 : _k.lineHeight
                : null;
        value.paragraphIndent = (definition.paragraphIndent
            ? DimensionToken.dimensionValueFromDefinitionOrReference(definition.paragraphIndent, referenceResolver, TokenType.paragraphSpacing)
            : ((_l = base === null || base === void 0 ? void 0 : base.value) === null || _l === void 0 ? void 0 : _l.paragraphIndent)
                ? (_m = base === null || base === void 0 ? void 0 : base.value) === null || _m === void 0 ? void 0 : _m.paragraphIndent
                : DimensionToken.dimensionValueFromDefinition("0", TokenType.paragraphSpacing));
        value.paragraphSpacing = (definition.paragraphSpacing
            ? DimensionToken.dimensionValueFromDefinitionOrReference(definition.paragraphSpacing, referenceResolver, TokenType.paragraphSpacing)
            : ((_o = base === null || base === void 0 ? void 0 : base.value) === null || _o === void 0 ? void 0 : _o.paragraphSpacing)
                ? (_p = base === null || base === void 0 ? void 0 : base.value) === null || _p === void 0 ? void 0 : _p.paragraphSpacing
                : DimensionToken.dimensionValueFromDefinition("0", TokenType.paragraphSpacing));
        value.paragraphIndent.referencedTokenId = null;
        // @ts-expect-error TS(2322): Type 'undefined' is not assignable to type 'string... Remove this comment to see the full error message
        value.referencedTokenId = undefined;
        value.textDecoration =
            TextDecorationToken.optionValueFromDefinitionOrReference((_q = definition.textDecoration) !== null && _q !== void 0 ? _q : (_r = base === null || base === void 0 ? void 0 : base.value) === null || _r === void 0 ? void 0 : _r.textDecoration, referenceResolver, TokenType.textDecoration);
        value.textCase = TextDecorationToken.optionValueFromDefinitionOrReference((_s = definition.textCase) !== null && _s !== void 0 ? _s : (_t = base === null || base === void 0 ? void 0 : base.value) === null || _t === void 0 ? void 0 : _t.textCase, referenceResolver, TokenType.textCase);
        return value;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Writing
    toWriteObject() {
        const baseData = this.toBaseWriteObject();
        const specificData = baseData;
        specificData.data = TypographyToken.valueToWriteObject(this.value);
        return specificData;
    }
    static valueToWriteObject(value) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        const prepareFontSizeValue = (fontSize) => {
            const { unit, measure } = fontSize;
            let fixedMeasure = measure;
            if (measure <= 0) {
                fixedMeasure = 0.01;
            }
            return {
                measure: fixedMeasure, // Font size must be > 0
                unit,
            };
        };
        const valueObject = !value.referencedTokenId
            ? {
                fontFamily: {
                    aliasTo: (_a = value.fontFamily.referencedTokenId) !== null && _a !== void 0 ? _a : undefined,
                    value: value.fontFamily.referencedTokenId
                        ? null
                        : value.fontFamily.text,
                },
                fontWeight: {
                    aliasTo: (_b = value.fontWeight.referencedTokenId) !== null && _b !== void 0 ? _b : undefined,
                    value: value.fontWeight.referencedTokenId
                        ? null
                        : value.fontWeight.text,
                },
                fontSize: {
                    aliasTo: (_c = value.fontSize.referencedTokenId) !== null && _c !== void 0 ? _c : undefined,
                    value: value.fontSize.referencedTokenId
                        ? null
                        : prepareFontSizeValue(value.fontSize),
                },
                letterSpacing: {
                    aliasTo: (_d = value.letterSpacing.referencedTokenId) !== null && _d !== void 0 ? _d : undefined,
                    value: value.letterSpacing.referencedTokenId
                        ? null
                        : {
                            measure: value.letterSpacing.measure,
                            unit: value.letterSpacing.unit,
                        },
                },
                paragraphIndent: {
                    aliasTo: (_e = value.paragraphIndent.referencedTokenId) !== null && _e !== void 0 ? _e : undefined,
                    value: value.paragraphIndent.referencedTokenId
                        ? null
                        : {
                            measure: value.paragraphIndent.measure,
                            unit: value.paragraphIndent.unit,
                        },
                },
                paragraphSpacing: {
                    aliasTo: (_f = value.paragraphSpacing.referencedTokenId) !== null && _f !== void 0 ? _f : undefined,
                    value: value.paragraphSpacing.referencedTokenId
                        ? null
                        : {
                            measure: value.paragraphSpacing.measure,
                            unit: value.paragraphSpacing.unit,
                        },
                },
                lineHeight: value.lineHeight
                    ? {
                        aliasTo: (_g = value.lineHeight.referencedTokenId) !== null && _g !== void 0 ? _g : undefined,
                        value: value.lineHeight.referencedTokenId
                            ? null
                            : {
                                measure: value.lineHeight.measure,
                                unit: value.lineHeight.unit,
                            },
                    }
                    : null,
                textCase: {
                    aliasTo: (_h = value.textCase.referencedTokenId) !== null && _h !== void 0 ? _h : undefined,
                    value: value.textCase.referencedTokenId
                        ? null
                        : value.textCase.value,
                },
                textDecoration: {
                    aliasTo: (_j = value.textDecoration.referencedTokenId) !== null && _j !== void 0 ? _j : undefined,
                    value: value.textDecoration.referencedTokenId
                        ? null
                        : value.textDecoration.value,
                },
            }
            : undefined;
        return {
            aliasTo: (_k = value.referencedTokenId) !== null && _k !== void 0 ? _k : undefined,
            // @ts-expect-error TS(2322): Type '{ fontFamily: { aliasTo: string | undefined;... Remove this comment to see the full error message
            value: valueObject,
        };
    }
}
